/**
 * ArchivePage — the public face of the archival world.
 *
 * Turns the knowledge graph into a legible, living artefact: total
 * persons, places, eras, documents, capsules; weekly growth on each.
 * The header narrative frames the product as "a journey through the
 * past" rather than a feature list, reinforcing the shared mission
 * whether the visitor ends up building their own tree or not.
 *
 * All data comes from /api/archive/stats (unauthenticated) so the same
 * component could later be embedded into the landing pages.
 */
import { useEffect, useState } from 'react'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'

export default function ArchivePage() {
  usePageTitle('The Archive')
  const [stats, setStats] = useState(null)
  const [topPlaces, setTopPlaces] = useState([])

  useEffect(() => {
    api.get('/archive/stats').then(setStats).catch(() => {})
    api.get('/archive/top-places').then(setTopPlaces).catch(() => {})
  }, [])

  return (
    <div className="archive-page">
      <div className="archive-page__hero">
        <div className="archive-page__eyebrow">The Archive</div>
        <h1 className="archive-page__title">
          A journey through <em>the past</em>
        </h1>
        <p className="archive-page__lead">
          Every person added, every place walked, every record analysed feeds
          one shared archival world. These numbers grow each week as families
          restore the lives that came before them.
        </p>
      </div>

      <div className="archive-stats">
        {/* Records is the headline stat — the Knowledge Graph index is
            orders of magnitude larger than any one user's tree, so it
            frames the scale of the archive before the per-tree numbers. */}
        <StatBlock
          label="Records"
          value={stats?.records.total}
          sub={stats ? [
            `${stats.records.wikidata.toLocaleString()} from Wikidata`,
            `${stats.records.gedcom.toLocaleString()} from GEDCOM`,
            stats.records.user > 0 && `${stats.records.user.toLocaleString()} from users`,
          ].filter(Boolean).join(' · ') : null}
          featured
        />
        <StatBlock
          label="Persons in trees"
          value={stats?.persons.total}
          sub={stats ? `${stats.persons.historical.toLocaleString()} historical` : null}
          delta={stats?.persons.last_7d}
        />
        <StatBlock
          label="Places"
          value={stats?.places.total}
          sub={stats ? `${stats.places.geocoded.toLocaleString()} geocoded` : null}
          delta={stats?.places.last_7d}
        />
        <StatBlock
          label="Eras"
          value={stats?.events.total}
          sub="verified historical events"
        />
        <StatBlock
          label="Worlds"
          value={stats?.capsules.published}
          sub={stats ? `${stats.capsules.total - stats.capsules.published} drafts in review` : null}
        />
        <StatBlock
          label="Documents"
          value={stats?.documents.total}
          sub={stats ? `${stats.documents.analysed.toLocaleString()} AI-analysed` : null}
          delta={stats?.documents.last_7d}
        />
        <StatBlock
          label="Mentions"
          value={stats?.mentions.total}
          sub="place mentions across documents and persons"
        />
      </div>

      {topPlaces.length > 0 && (
        <div className="archive-page__section">
          <div className="archive-page__section-label">Most-mentioned places</div>
          <div className="archive-page__places">
            {topPlaces.map(p => (
              <span key={p.id} className="archive-place-pill">
                {p.name_raw} <span className="archive-place-pill__count">{p.mention_count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="archive-page__section">
        <div className="archive-page__section-label">How the world grows</div>
        <div className="archive-page__how">
          <div className="archive-how__step">
            <div className="archive-how__num">1</div>
            <div className="archive-how__text">
              <strong>Families add their trees.</strong> Persons, places and
              dates enter the graph.
            </div>
          </div>
          <div className="archive-how__step">
            <div className="archive-how__num">2</div>
            <div className="archive-how__text">
              <strong>Documents come alive.</strong> Birth certificates, letters
              and photographs are read by AI. Every extracted person feeds the
              canonical index; every place is geocoded and added to the atlas.
            </div>
          </div>
          <div className="archive-how__step">
            <div className="archive-how__num">3</div>
            <div className="archive-how__text">
              <strong>Eras come into focus.</strong> Curators — human and AI —
              write capsules that capture what life was like in a time and
              place. Every user whose ancestor was there can step into that
              world.
            </div>
          </div>
          <div className="archive-how__step">
            <div className="archive-how__num">4</div>
            <div className="archive-how__text">
              <strong>The past enriches the present.</strong> Verified data
              flows back to every tree. One researcher's gain becomes the
              whole community's knowledge.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StatBlock({ label, value, sub, delta, featured }) {
  const shown = value != null ? Number(value).toLocaleString() : '—'
  return (
    <div className={`archive-stat${featured ? ' archive-stat--featured' : ''}`}>
      <div className="archive-stat__value">{shown}</div>
      <div className="archive-stat__label">{label}</div>
      {sub && <div className="archive-stat__sub">{sub}</div>}
      {delta > 0 && (
        <div className="archive-stat__delta">+{delta} this week</div>
      )}
    </div>
  )
}
